import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { SessionInfo, OrganisationDetail, MembershipRole, UUID } from '../types';
import { api, ApiError } from '../services/api';

interface AuthContextType {
  session: SessionInfo | null;
  activeOrg: OrganisationDetail | null;
  activeRole: MembershipRole | null;
  loading: boolean;
  error: string | null;
  refreshSession: () => Promise<SessionInfo | null>;
  switchOrg: (orgId: UUID) => Promise<void>;
  login: () => void;
  logout: () => void;
  switchAccount: () => void;
  setActiveOrgDetail: React.Dispatch<React.SetStateAction<OrganisationDetail | null>>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [activeOrg, setActiveOrg] = useState<OrganisationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshSession = useCallback(async (): Promise<SessionInfo | null> => {
    try {
      const data = await api.auth.getSession();
      setSession(data);
      setError(null);

      if (data.active_org_id) {
        try {
          const org = await api.orgs.get(data.active_org_id);
          setActiveOrg(org);
        } catch {
          setActiveOrg(null);
        }
      } else {
        setActiveOrg(null);
      }
      return data;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setSession(null);
        setActiveOrg(null);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load session');
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  const switchOrg = async (orgId: UUID) => {
    setLoading(true);
    try {
      await api.auth.switchOrg(orgId);
      await refreshSession();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to switch organisation');
      setLoading(false);
      throw err;
    }
  };

  const login = () => {
    window.location.href = api.auth.loginUrl;
  };

  const logout = () => {
    window.location.href = api.auth.logoutUrl;
  };

  // Drop the Nexus session first, then send the user to the CYRN Auth account
  // chooser so they can continue as a different identity.
  const switchAccount = () => {
    window.location.href = api.auth.switchUrl;
  };

  const activeMembership = session?.memberships.find(
    (m) => m.organisation?.id === session.active_org_id
  );
  const activeRole = activeMembership?.role || null;

  return (
    <AuthContext.Provider
      value={{
        session,
        activeOrg,
        activeRole,
        loading,
        error,
        refreshSession,
        switchOrg,
        login,
        logout,
        switchAccount,
        setActiveOrgDetail: setActiveOrg,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
